import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  FormControlLabel, FormLabel, Radio, RadioGroup, Stack, TextField,
} from '@mui/material';
import type { ClosedOutcome } from '../../api/types';

export interface CloseResult {
  outcome: ClosedOutcome;
  lostReason: string | null;
}

/** Closing is the one stage move that needs more than a drop: an outcome is mandatory, and a
 *  loss must say why — that reason is the most useful thing in the whole record later. */
export function CloseOpportunityDialog({
  open, title, onConfirm, onCancel,
}: {
  open: boolean;
  title: string;
  onConfirm: (result: CloseResult) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [outcome, setOutcome] = useState<ClosedOutcome>('won');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setOutcome('won');
      setReason('');
      setTouched(false);
    }
  }, [open]);

  const reasonMissing = outcome === 'lost' && reason.trim().length === 0;

  const confirm = () => {
    setTouched(true);
    if (reasonMissing) return;
    onConfirm({ outcome, lostReason: outcome === 'lost' ? reason.trim() : null });
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>{t('forms.close.title', { title })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <FormControl>
            <FormLabel id="close-outcome-label">{t('forms.close.outcome')}</FormLabel>
            <RadioGroup
              row
              aria-labelledby="close-outcome-label"
              value={outcome}
              onChange={(e) => setOutcome(e.target.value as ClosedOutcome)}
            >
              <FormControlLabel value="won" control={<Radio data-testid="outcome-won" />} label={t('outcome.won')} />
              <FormControlLabel value="lost" control={<Radio data-testid="outcome-lost" />} label={t('outcome.lost')} />
            </RadioGroup>
          </FormControl>

          {outcome === 'lost' && (
            <TextField
              label={t('forms.close.whyLost')}
              required
              multiline
              minRows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onBlur={() => setTouched(true)}
              error={touched && reasonMissing}
              helperText={
                touched && reasonMissing
                  ? t('forms.close.reasonRequired')
                  : t('forms.close.reasonHint')
              }
              inputProps={{ dir: 'auto', maxLength: 1000, 'data-testid': 'lost-reason' }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel}>{t('common.cancel')}</Button>
        <Button variant="contained" onClick={confirm} data-testid="confirm-close">
          {outcome === 'won' ? t('forms.close.confirmWon') : t('forms.close.confirmLost')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
